import React, { DragEvent, RefObject, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { PropertyType } from '@producer/properties';

import { CoverImage } from '../../../components/cover-image/cover-image';
import { SelectDrawer } from './select-drawer';
import { InputDrawer } from './input-drawer';
import { TextAreaDrawer } from '../../../components/drawer-inputs/text-area-drawer';
import { NumberInputDrawer } from '../../../components/drawer-inputs/number-input-drawer';
import { LocationSelect } from './location-select';
import { StoryboardItemHeader } from './StoryboardItemHeader';
import { Bloc } from '../../../routes/project/components/step-drawer';
import { CharactersSelect } from './characters-select';
import { formatPicture } from '../../../core/services/image.service';
import { getModifier } from '../../../core/services/event.service';
import { Shot, Storyboard, UpdateShotsInput } from '../../../app/entities';
import { TimeInputDrawer } from '../../../components/drawer-inputs/time-input-drawer';
import { LimitReachedModal } from '../../../components/dialogs/limit-reached.modal';
import { useSelection, type Modifier } from '../../../core/contexts/selection.context';
import { bulkUpdateStore } from '../../../core/stores/bulk-update';
import { TagsViewer } from '../../tags/components/tags-viewer';
import { openModal } from '../../../core/modal/open-modal';
import { compareArrays } from '../../../lib/utils/compare-two-arrays';

import { Property } from '../../../app/entities/property';
import { MultiSelectItem } from './multi-select.item';

import styles from './shot-card.module.scss';
export interface ShotCardProps {
  shotId: string;
  projectId: string;
  storyboardId: string;
  active?: boolean;
  position?: number;
  style?: any;
  onClick?: (shotId: string, modifier?: Modifier) => void;

  storyboardPreferences?: {
    zoom: number;
  };

  properties?: Property[];

  ref: RefObject<HTMLDivElement>;
}

export const ShotCard: React.FC<ShotCardProps> = observer(
  ({
    shotId,
    projectId,
    storyboardId,
    position,
    active,
    style,
    onClick,
    storyboardPreferences,
    /**
     * Last-resort guard: a storyboard whose shot-list view is missing from the entity pool has no
     * properties, and rendering none beats crashing the whole board (PRO-1051).
     */
    properties = [],
    ref,
    ...props
  }) => {
    const { elements: selectedShotsIds } = useSelection();

    const storyboard = Storyboard.getOne(storyboardId)!;
    const shot = Shot.getOne(shotId);

    if (!shot) {
      return null;
    }

    const isBulkUpdate = selectedShotsIds && selectedShotsIds.length > 1;

    const [expand, setExpand] = useState(false);

    const handleSingleEdit = (data: UpdateShotsInput) => {
      if (data.tagsIds) {
        shot?.updateTags(data.tagsIds);
        return;
      }

      shot && shot.update(data!);
    };

    const handleEdit = (data: any) => {
      if (!isBulkUpdate) {
        return handleSingleEdit(data);
      }

      const { tagsIds, ...rest } = data;

      bulkUpdateStore.handleShotsBulkUpdate(rest, selectedShotsIds, shot!);
    };

    const handleEditProperty = (propertyId: string, value: any) => {
      if (!isBulkUpdate) {
        return handleSingleEdit({
          properties: {
            ...shot.properties,
            [propertyId]: value,
          },
        });
      }

      bulkUpdateStore.handleShotsBulkUpdate(
        { properties: { [propertyId]: value } },
        selectedShotsIds,
        shot!,
      );
    };

    const handleCreateShot = async (file?: File) => {
      if (!shot?.project?.canAddShot()) {
        return openModal(LimitReachedModal, {
          type: 'shots',
          eventName: 'Shots exceed 10',
        });
      }

      if (!projectId) {
        return;
      }

      let frame;
      if (file) {
        const options = { width: 640, height: 380 };
        frame = await formatPicture(file, options);
      }

      await Shot.createShot(
        {
          projectId,
          position: position || 0,
          file: frame,
        },
        storyboard?._id,
      );
    };

    const handleDragEnter = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setExpand(true);
    };

    const handleDragLeave = (e: React.MouseEvent<HTMLDivElement>) => {
      e.preventDefault();
      setExpand(false);
    };

    const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
    };

    const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.persist();
      setExpand(false);

      const files = Array.from(e.dataTransfer.files);

      for (const file of files) {
        await handleCreateShot(file);
      }
    };

    const handleClick = async () => {
      handleCreateShot();
    };

    const currTags = [...shot.tagsIds];

    const renderProperty = (property: Property, index: number) => {
      if (!shot) return;

      const value = shot.getProperty(property._id);

      switch (property.type) {
        case PropertyType.SingleSelect:
          if (property.resourceType === 'Location') {
            return (
              <LocationSelect
                key={property._id}
                projectId={projectId}
                onChange={(locationId) => handleEdit({ locationId })}
                selectedLocationId={shot.locationId}
              />
            );
          }

          return (
            <SelectDrawer
              key={property._id}
              value={value}
              onSelect={(value) => handleEditProperty(property._id, value)}
              options={property.options.map((option) => ({ label: option, value: option }))}
              label={property.name}
            />
          );
        case PropertyType.MultiSelect:
          if (property.resourceType === 'Character') {
            return (
              <CharactersSelect
                key={property._id}
                isShotsBulkUpdate={selectedShotsIds && selectedShotsIds.length > 1}
                handleShotsBulkUpdate={({ characterIdToRemove, characterIdToAdd }) => {
                  handleEdit({ characterIdToRemove, characterIdToAdd });
                }}
                shot={shot}
              />
            );
          } else if (property.resourceType === 'Tag') {
            return (
              <Bloc key={property._id}>
                <label>Tags</label>

                <TagsViewer
                  targetId={shot._id}
                  tagsIds={shot.tagsIds}
                  targetType="shot"
                  overflow="wrap"
                  alignment="start"
                  onSelect={(tagsIds) => {
                    const { added, deleted } = compareArrays(currTags, tagsIds);
                    const tagIdToRemove = deleted.length > 0 ? deleted[0] : undefined;
                    const tagIdToAdd = added.length > 0 ? added[0] : undefined;

                    handleEdit?.({ tagIdToAdd, tagIdToRemove, tagsIds });
                  }}
                />
              </Bloc>
            );
          }

          return (
            <MultiSelectItem
              key={property._id}
              value={value && Array.isArray(value) ? value : []}
              onSelect={(value) => handleEditProperty(property._id, value)}
              options={property.options.map((option) => ({ label: option, value: option }))}
              label={property.name}
            />
          );
        case PropertyType.SmallText:
          return (
            <InputDrawer
              key={property._id}
              value={value}
              label={property.name}
              onChange={(value) => handleEditProperty(property._id, value)}
            />
          );
        case PropertyType.Number:
          return (
            <NumberInputDrawer
              value={value}
              canNeg={false}
              onChange={(value) => handleEditProperty(property._id, value)}
              label={property.name}
            />
          );
        case PropertyType.LongText:
          return (
            <TextAreaDrawer
              key={property._id}
              label={property.name}
              value={shot.getProperty(property._id)}
              onChange={(value) => handleEditProperty(property._id, value)}
            />
          );
        case PropertyType.Duration:
          /**
           * Only work with estimated time for now
           */
          return (
            <TimeInputDrawer
              key={property._id}
              value={shot.estimatedTime}
              onChange={(estimatedTime) => handleEdit({ estimatedTime })}
              label={property.name}
            />
          );
        default:
          return;
      }
    };

    const handleShotClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      const modifier = getModifier(e.nativeEvent);

      if (!shot) return;

      onClick && onClick(shot._id, modifier);
    };

    if (!shot) return null;

    const renderVoiceOver = () => {
      const voiceOverProperty = properties.find((p) => p.key === 'voiceOver');
      const voiceOverValue = shot.getProperty(voiceOverProperty?._id);

      return (
        voiceOverValue &&
        storyboard &&
        storyboardPreferences &&
        storyboardPreferences.zoom > 4 && (
          <div className={styles['subtitle-container']}>
            <div
              className={styles['subtitle']}
              dangerouslySetInnerHTML={{
                __html: voiceOverValue.replaceAll('\n', '<br>'),
              }}
            />
          </div>
        )
      );
    };

    return (
      <div
        className={styles['wrapper']}
        data-active={!!active}
        data-zoom={storyboardPreferences?.zoom}
      >
        <div className={styles['page-container']} ref={ref}>
          <div
            className={styles['insert-overlay']}
            onDrop={handleDrop}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            data-expand={expand}
          >
            <div className={styles['insert-tile']} onClick={handleClick}>
              +
            </div>
          </div>

          <div
            className={styles['shot-container']}
            onClick={handleShotClick}
            style={style}
            {...props}
          >
            <div className={styles['cover']}>
              <CoverImage
                src={shot.cover?.src || ''}
                title={shot.title}
                progress={shot.coverGenerateProgress}
                onEdit={(file) => handleEdit({ file })}
              />

              <StoryboardItemHeader
                shot={shot}
                zoomLevel={storyboardPreferences?.zoom || 1}
                storyboardId={storyboardId}
                position={position}
              />

              {renderVoiceOver()}
            </div>

            <div className={styles['block']}>{properties.map(renderProperty)}</div>
          </div>
        </div>
      </div>
    );
  },
);
